import { uniqBy } from 'lodash-es';
import { descend, lensProp, over, pipe, prop, propEq, reject } from 'ramda';
import {
  getMessageDetails,
  getMessagesList,
  getNotificationsInitialData,
  getNotificationsList,
  readNotification,
  skipNotification
} from '../api';
import { normalizeMessages } from './normalizers';
import { useNotificationCenterStore } from './store';

export const actionsCreator = ({ setState, getState }) => ({
  SET_IS_NOTIFICATIONS_LIST_OPENED: (isOpened) => {
    const state = getState();

    setState({
      isNotificationsListOpened:
        typeof isOpened === 'function'
          ? isOpened(state.isNotificationsListOpened)
          : isOpened
    });
  },
  ADD_CRITICAL_NOTIFICATION: ({ notification, isAddToQueue }) => {
    const state = getState();

    setState({
      criticalNotifications: isAddToQueue
        ? [...state.criticalNotifications, notification]
        : [notification]
    });
  },
  SET_HAS_NOTIFICATIONS: (hasNotifications) => {
    setState({ hasNotifications });
  },
  REMOVE_NOTIFICATION_BY_MESSAGE_ID: (messageId) => {
    const state = getState();

    const updatedState = pipe(
      over(lensProp('notifications'), reject(propEq(messageId, 'messageId'))),
      over(
        lensProp('criticalNotifications'),
        reject(propEq(messageId, 'messageId'))
      )
    )(state);

    setState(updatedState);
  },
  ADD_NOTIFICATIONS: ({ notifications, notificationsPagination }) => {
    const state = getState();

    const nextNotifications = uniqBy(
      [...state.notifications, ...notifications].sort(descend(prop('date'))),
      'notificationId'
    );

    setState({
      notifications: nextNotifications,
      notificationsPagination: {
        ...state.notificationsPagination,
        ...notificationsPagination
      }
    });
  },
  FETCH_MESSAGES_LIST_DATA: async ({ filter, pagination }) => {
    try {
      const state = getState();
      setState({ isFetchingMessagesList: true });

      const messagesListData = await getMessagesList({ filter, pagination });
      const { items, pagination: paginationResponse } = messagesListData;
      const { totalCount } = paginationResponse;

      setState({
        messages: normalizeMessages(items),
        messagesListPaginationData: {
          ...state.messagesListPaginationData,
          offset: items.length,
          totalCount
        }
      });
    } catch (e) {
      console.error(e);
    } finally {
      setState({ isFetchingMessagesList: false });
    }
  },
  LOAD_MORE_MESSAGES: async ({ filter, pagination }) => {
    try {
      const state = getState();
      setState({ isFetchingMoreMessages: true });

      const messagesListData = await getMessagesList({ filter, pagination });
      const { items, pagination: paginationResponse } = messagesListData;
      const { totalCount } = paginationResponse;
      const { ids, data } = normalizeMessages(items);

      setState({
        messages: {
          ids: [...state.messages.ids, ...ids],
          data: { ...state.messages.data, ...data }
        },
        messagesListPaginationData: {
          ...state.messagesListPaginationData,
          offset: [...state.messages.ids, ...ids].length,
          totalCount
        }
      });
    } catch (e) {
      console.error(e);
    } finally {
      setState({ isFetchingMoreMessages: false });
    }
  },
  FETCH_MESSAGE_DETAILS: async (messageId) => {
    try {
      setState({ isFetchingMessageDetails: true });

      const messageData = await getMessageDetails(messageId);
      notificationCenterActions.REMOVE_NOTIFICATION_BY_MESSAGE_ID(messageId);

      setState({
        messageDetails: messageData
      });
    } catch (e) {
      console.error(e);
    } finally {
      setState({ isFetchingMessageDetails: false });
    }
  },
  FETCH_NOTIFICATIONS_INITIAL_DATA: async () => {
    try {
      const nfInitData = await getNotificationsInitialData();
      const { notifications = [], hasNotifications } = nfInitData;

      setState({ hasNotifications, criticalNotifications: notifications });
    } catch (e) {
      throw new Error(e);
    }
  },
  FETCH_NOTIFICATIONS_LIST: async ({ pagination }) => {
    try {
      setState({ isLoadingNotifications: true });
      const { items, pagination: notificationsPagination } =
        await getNotificationsList({ pagination });

      notificationCenterActions.ADD_NOTIFICATIONS({
        notifications: items,
        notificationsPagination
      });
    } catch (e) {
      console.error(e);
    } finally {
      setState({ isLoadingNotifications: false });
    }
  },
  READ_NOTIFICATION: async (notificationId) => {
    const state = getState();

    setState({
      notifications: state.notifications.filter(
        (n) => n.notificationId !== notificationId
      ),
      criticalNotifications: state.criticalNotifications.filter(
        (n) => n.notificationId !== notificationId
      )
    });

    try {
      await readNotification({ notificationId });
    } catch (e) {
      console.error(e);
    }
  },
  SKIP_NOTIFICATION: async (notificationId) => {
    const state = getState();
    setState({
      criticalNotifications: state.criticalNotifications.filter(
        (n) => n.notificationId !== notificationId
      )
    });

    try {
      await skipNotification({ notificationId });
    } catch (e) {
      console.error(e);
    }
  }
});

export const notificationCenterActions = actionsCreator(
  useNotificationCenterStore
);
